import React, { useEffect, useState } from "react";
import "./Help.css";

const Help = () => {
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingArticles, setLoadingArticles] = useState(false);
  const [loadingArticle, setLoadingArticle] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchActive, setSearchActive] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const loadCategories = async () => {
      setLoadingCategories(true);
      setError("");
      try {
        const response = await fetch("/backend/help/categories", {
          credentials: "include",
          cache: "no-store",
        });
        if (!response.ok) throw new Error("Unable to load Help categories.");
        const data = await response.json();
        if (!cancelled) {
          setCategories(Array.isArray(data) ? data : data.categories || []);
        }
      } catch (err) {
        console.error("Help categories error:", err);
        if (!cancelled) setError("Scoldy Help could not be loaded right now. Please try again.");
      } finally {
        if (!cancelled) setLoadingCategories(false);
      }
    };
    loadCategories();
    return () => { cancelled = true; };
  }, []);

  const openCategory = async (category) => {
    setSelectedCategory(category);
    setSelectedArticle(null);
    setSearchText("");
    setSearchResults([]);
    setSearchActive(false);
    setArticles([]);
    setLoadingArticles(true);
    setError("");
    try {
      const response = await fetch("/backend/help/articles", {
        credentials: "include",
        cache: "no-store",
      });
      if (!response.ok) throw new Error("Unable to load Help articles.");
      const data = await response.json();
      let rows = Array.isArray(data) ? data : data.articles || [];
      rows = rows.filter(
        (article) =>
          Number(article.category_id) === Number(category.id) ||
          Number(article.category?.id) === Number(category.id)
      );
      setArticles(rows);
    } catch (err) {
      console.error("Help articles error:", err);
      setError("The articles for this Help category could not be loaded.");
    } finally {
      setLoadingArticles(false);
    }
  };

  const openArticle = async (article) => {
    if (!article?.slug) return;
    setLoadingArticle(true);
    setError("");
    try {
      const response = await fetch(
        `/backend/help/articles/${encodeURIComponent(article.slug)}`,
        { credentials: "include", cache: "no-store" }
      );
      if (!response.ok) throw new Error("Unable to load Help article.");
      const data = await response.json();
      setSelectedArticle(data.article || data);
    } catch (err) {
      console.error("Help article error:", err);
      setError("That Help article could not be loaded.");
    } finally {
      setLoadingArticle(false);
    }
  };

  const runSearch = async (event) => {
    if (event) event.preventDefault();
    const query = searchText.trim();
    if (!query) {
      setSearchResults([]);
      setSearchActive(false);
      setSelectedArticle(null);
      setError("");
      return;
    }
    setSearching(true);
    setSearchActive(true);
    setSelectedCategory(null);
    setSelectedArticle(null);
    setArticles([]);
    setError("");
    try {
      const response = await fetch(
        `/backend/help/search?q=${encodeURIComponent(query)}`,
        { credentials: "include", cache: "no-store" }
      );
      if (!response.ok) throw new Error("Unable to search Help.");
      const data = await response.json();
      setSearchResults(Array.isArray(data) ? data : data.articles || data.results || []);
    } catch (err) {
      console.error("Help search error:", err);
      setError("Scoldy Help search could not be completed right now.");
    } finally {
      setSearching(false);
    }
  };

  const showCategories = () => {
    setSelectedCategory(null);
    setSelectedArticle(null);
    setArticles([]);
    setSearchText("");
    setSearchResults([]);
    setSearchActive(false);
    setError("");
  };

  useEffect(() => {
    const handleResetHelp = () => {
      setSelectedCategory(null);
      setSelectedArticle(null);
      setArticles([]);
      setSearchText("");
      setSearchResults([]);
      setSearchActive(false);
      setError("");

      const mainContent = document.querySelector(".main-content");
      if (mainContent) mainContent.scrollTop = 0;
    };

    window.addEventListener("resetHelpHome", handleResetHelp);
    return () => {
      window.removeEventListener("resetHelpHome", handleResetHelp);
    };
  }, []);

  const renderAnswer = (answer) =>
    String(answer || "")
      .split(/\n{2,}/)
      .filter((p) => p.trim())
      .map((p, i) => <p key={i}>{p.trim()}</p>);

  return (
    <div className="help-page">
      <div className="help-header">
        <h1>Scoldy Help</h1>
        <p>Find answers about using Scoldy, your account, Communities, Premium, Boost, safety and more.</p>
        <form className="help-search-form" onSubmit={runSearch}>
          <input
            className="help-search-box"
            type="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search Scoldy Help..."
            aria-label="Search Scoldy Help"
          />
          <button type="submit" disabled={searching}>
            {searching ? "Searching..." : "Search"}
          </button>
        </form>
      </div>

      {error && <div className="help-error">{error}</div>}

      {selectedArticle ? (
        <section className="help-article-view">
          <button
            type="button"
            className="help-back-button"
            onClick={() => setSelectedArticle(null)}
          >
            ← Back
          </button>
          {loadingArticle ? (
            <p>Loading article...</p>
          ) : (
            <>
              <h2>{selectedArticle.title}</h2>
              <div className="help-answer">{renderAnswer(selectedArticle.answer)}</div>
            </>
          )}
        </section>
      ) : selectedCategory ? (
        <section className="help-category-view">
          <button type="button" className="help-back-button" onClick={showCategories}>
            ← All Help Categories
          </button>
          <h2>{selectedCategory.name}</h2>
          {selectedCategory.description && <p>{selectedCategory.description}</p>}
          {loadingArticles ? (
            <p>Loading articles...</p>
          ) : articles.length ? (
            <div className="help-article-list">
              {articles.map((article) => (
                <button
                  type="button"
                  className="help-article-link"
                  key={article.id || article.slug}
                  onClick={() => openArticle(article)}
                >
                  {article.title}
                </button>
              ))}
            </div>
          ) : (
            <p>No published Help articles were found in this category.</p>
          )}
        </section>
      ) : searchActive ? (
        <section className="help-search-results">
          <div className="help-section-heading">
            <h2>Search Results</h2>
            <button type="button" className="help-text-button" onClick={showCategories}>
              Clear search
            </button>
          </div>
          {searching ? (
            <p>Searching...</p>
          ) : searchResults.length ? (
            <div className="help-article-list">
              {searchResults.map((article) => (
                <button
                  type="button"
                  className="help-article-link"
                  key={article.id || article.slug}
                  onClick={() => openArticle(article)}
                >
                  {article.title}
                </button>
              ))}
            </div>
          ) : (
            <p>No Help articles matched your search. Try different words or contact Scoldy Support if you still need help.</p>
          )}
        </section>
      ) : (
        <section className="help-categories">
          <h2>Help Categories</h2>
          {loadingCategories ? (
            <p>Loading Help categories...</p>
          ) : categories.length ? (
            <div className="help-category-grid">
              {categories.map((category) => (
                <button
                  type="button"
                  className="help-category-card"
                  key={category.id || category.slug}
                  onClick={() => openCategory(category)}
                >
                  <strong>{category.name}</strong>
                  {category.description && <span>{category.description}</span>}
                </button>
              ))}
            </div>
          ) : (
            <p>No Help categories are available right now.</p>
          )}
        </section>
      )}
    </div>
  );
};

export default Help;
